<script setup lang="ts">
import { computed, ref, shallowRef } from 'vue';
import axios from 'axios';
import Decimal from 'decimal.js';
import i18n from '@/localisation/i18n';
import PrettyDate from '@/modules/common/components/format-date/PrettyDate.vue';
import { PRICE_PRECISION } from '@/modules/common/constants/precision';
import wait from '@/modules/common/services/wait';
import { ValueAtRiskResponse, normalizeValueAtRiskResponse } from '@/utils/api/analytics';
import { getPercentageAsString, getPriceAsString } from '@/utils/helpers/auction-numbers';
import { errorString } from '@/utils/helpers/rest-response';

enum State {
  Input,
  Submitted,
  DisplayResult,
}

const tableColumns = [
  {
    text: i18n.t('CUSIP'),
    value: 'cusip',
    class: 'text-truncate',
    cellClass: 'text-truncate',
  },
  {
    text: i18n.t('Ticker'),
    value: 'ticker',
    class: 'text-truncate',
    cellClass: 'text-truncate',
    width: 100,
  },
  {
    text: i18n.t('Quantity'),
    value: 'quantity',
    class: 'text-truncate',
    cellClass: 'text-truncate is-numeric',
    align: 'end',
  },
  {
    text: i18n.t('Close Price'),
    value: 'closePrice',
    class: 'text-truncate',
    cellClass: 'text-truncate is-numeric',
    align: 'end',
  },
  {
    text: i18n.t('Notional Value'),
    value: 'notional',
    class: 'text-truncate',
    cellClass: 'text-truncate is-numeric',
    align: 'end',
  },
  {
    text: i18n.t('VaR'),
    value: 'valueAtRisk',
    class: 'text-truncate',
    cellClass: 'text-truncate is-numeric',
    align: 'end',
  },
  {
    text: i18n.t('analytics.VaR.haircut'),
    value: 'haircut',
    class: 'text-truncate',
    cellClass: 'text-truncate is-numeric',
    align: 'end',
  },
  {
    text: i18n.t('% of total VaR'),
    value: 'utilization',
    class: 'text-truncate',
    cellClass: 'text-truncate utilization',
    align: 'end',
  },
];

const state = ref<State>(State.Input);
const calcError = ref<string | null>(null);
const file = shallowRef<File | null>(null);
const varResult = shallowRef<ValueAtRiskResponse | undefined>(undefined);

const totalNotional = computed<Decimal>(() => {
  const zero = new Decimal(0);
  return varResult.value
    ? varResult.value.positions.reduce((s, p) => s.add(p.notional), zero)
    : zero;
});

async function calculate(): Promise<void> {
  if (file.value === null) {
    calcError.value = i18n.t('analytics.VaR.needFile') as string;
    return;
  }

  const f = file.value;
  file.value = null;
  state.value = State.Submitted;
  calcError.value = null;
  const minimumWait = wait(500);

  try {
    const formData = new FormData();
    formData.append('file', f);
    const { data } = await axios.post<ValueAtRiskResponse>('/api/1/analytics/var/calc', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    normalizeValueAtRiskResponse(data);
    varResult.value = data;
    await minimumWait;
    state.value = State.DisplayResult;
  } catch (e) {
    state.value = State.Input;
    calcError.value = errorString(e as Error);
  }
}

function formatPrice(price: number | Decimal): string {
  return getPriceAsString(price, PRICE_PRECISION);
}
</script>

<template>
  <VContainer>
    <template v-if="state === State.Input || state === State.DisplayResult">
      <VForm novalidate @submit.prevent>
        <VRow>
          <VCol>
            <VFileInput
              v-model="file"
              data-test-id="var-calculator-file-input"
              :error-messages="calcError"
              :label="$t('analytics.VaR.uploadLabel')"
              truncate-length="50"
              @change="calculate()"
            ></VFileInput>
          </VCol>
          <VCol cols="2">
            <VBtn class="mt-2" @click="calculate()">{{ $t('analytics.VaR.calculate') }}</VBtn>
          </VCol>
          <VCol cols="6">
            <p>
              {{ $t('analytics.VaR.explanation.0') }} <br />
              {{ $t('analytics.VaR.explanation.1') }} <br />
              {{ $t('analytics.VaR.explanation.2') }}
            </p>
          </VCol>
        </VRow>
      </VForm>
    </template>
    <template v-if="state === State.Submitted">
      <VRow>
        <VCol align="center">
          <fulfilling-bouncing-circle-spinner
            :animation-duration="1500"
            color="#4caf50"
            :size="50"
          />
        </VCol>
      </VRow>
    </template>
    <template v-if="state === State.DisplayResult">
      <VRow v-if="varResult">
        <VCol>
          <p>
            Total Notional: ${{ formatPrice(totalNotional) }} <br />
            {{ $t('analytics.VaR.totalValueAtRisk') }}: ${{
              formatPrice(varResult.valueAtRisk)
            }}
            (<PrettyDate always-long :when="varResult.date" />) <br />
            {{ $t('analytics.VaR.haircut') }}:
            {{ getPercentageAsString(varResult.valueAtRisk.div(totalNotional).mul(100.0)) }}
          </p>
        </VCol>
      </VRow>
      <VRow>
        <VCol>
          <VDataTable
            dense
            disable-pagination
            fixed-header
            :headers="tableColumns"
            :items="varResult?.positions"
            no-hide-default-footer
            sort-by="notional"
            sort-desc
          >
            <template #[`item.closePrice`]="{ item }">
              ${{ formatPrice(item.closePrice) }}
            </template>
            <template #[`item.notional`]="{ item }"> ${{ formatPrice(item.notional) }} </template>
            <template #[`item.valueAtRisk`]="{ item }">
              ${{ formatPrice(item.valueAtRisk) }}
            </template>
            <template #[`item.haircut`]="{ item }">
              {{ getPercentageAsString(item.valueAtRisk.div(item.notional).mul(100.0)) }}
            </template>
            <template #[`item.utilization`]="{ item }">
              {{ getPercentageAsString(item.utilization * 100.0) }}
            </template>
          </VDataTable>
        </VCol>
      </VRow>
    </template>
  </VContainer>
</template>

<style lang="scss" scoped></style>
